import React from 'react';
import {
  TreeItem,
  treeItemClasses,
  TreeItemContentProps,
  TreeView,
  useTreeItem,
} from '@mui/lab';
import { Box, Checkbox } from '@mui/material';
import { Typography } from '@platform-ui/design-system';
import styled from '@emotion/styled';
import clsx from 'clsx';
import { arrayToTree } from 'performant-array-to-tree';
import { ArrowDropDown, ArrowRight } from '@mui/icons-material';
import { MultiOrgOption } from '../hooks/useMultiOrgHierarchy';
import { decorateOrgsForTree, highlighted, memoizedKeyBy } from './utils';
import { useTranslation } from 'react-i18next';

const StyledTreeItem = styled((props) => <TreeItem {...props} />)((props) => ({
  [`& .${treeItemClasses.content}`]: {
    '&:hover &:not(Mui-disabled)': {
      backgroundColor: 'rgba(222, 236, 255, 0.5)',
    },
    '&.Mui-selected': {
      backgroundColor: `rgba(222, 236, 255, 0.5)`,
    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 0,
    [`& .${treeItemClasses.content}`]: {
      paddingLeft: `${props.level * 24}px`,
    },
  },
}));

type OrgTreeProps = {
  organizations: MultiOrgOption[];
  userOrgIds: string[];
  searchTerm: string;
  selectedOrgIds: string[];
  onOrgChange: (orgIds: string[]) => void;
  workingOrgId?: string;
};

export const OrgTree: React.FC<OrgTreeProps> = ({
  organizations,
  userOrgIds,
  searchTerm,
  selectedOrgIds,
  onOrgChange: handleOrgChange,
  workingOrgId,
}) => {
  const { t } = useTranslation();
  const filteredOrgs = decorateOrgsForTree(
    organizations,
    userOrgIds,
    searchTerm,
    selectedOrgIds,
  );
  const tree = arrayToTree(filteredOrgs, {
    id: 'organizationId',
    parentId: 'parentId',
  });
  const orgMap = memoizedKeyBy(filteredOrgs, 'organizationId');

  const TreeNode = React.forwardRef((props: TreeItemContentProps, ref) => {
    const {
      classes,
      className,
      label,
      nodeId,
      icon: iconProp,
      expansionIcon,
      displayIcon,
    } = props;

    const {
      expanded,
      focused,
      handleExpansion,
      handleSelection: handleTreeSelection,
      preventSelection,
    } = useTreeItem(nodeId);

    const icon = iconProp || expansionIcon || displayIcon;

    const org = orgMap[nodeId];
    const handleSelection = (e) => {
      if (org.disabled || org.organizationId === workingOrgId) {
        preventSelection(e);
      } else {
        handleTreeSelection(e);
        if (org.selected) {
          const newOrgs = selectedOrgIds.filter((id) => id !== nodeId);
          if (newOrgs.length) {
            handleOrgChange(newOrgs);
          }
        } else {
          handleOrgChange([...selectedOrgIds, nodeId]);
        }
      }
      e.stopPropagation();
    };

    return (
      <Box
        ref={ref}
        sx={{ height: '40px', width: 1, boxSizing: 'border-box' }}
        className={clsx(className, classes.root, {
          [classes.expanded]: expanded,
          [classes.selected]: org.selected,
          [classes.focused]: focused,
          [classes.disabled]: org.disabled,
        })}
        onClick={handleSelection}
      >
        <Box
          onClick={(e) => {
            handleExpansion(e);
            preventSelection(e);
            e.stopPropagation();
          }}
          className={classes.iconContainer}
        >
          {icon}
        </Box>
        <Checkbox
          disabled={org.disabled || org.organizationId === workingOrgId}
          onClick={(e) => {
            preventSelection(e);
            e.stopPropagation();
            handleSelection(e);
          }}
          checked={org.selected}
          sx={{ padding: '8px' }}
        />
        <Typography noWrap variant="body2">
          {typeof label === 'string'
            ? org.organizationId === workingOrgId
              ? `${highlighted(label, searchTerm)} (${t('MultiOrg.Default')})`
              : highlighted(label, searchTerm)
            : label}
        </Typography>
      </Box>
    );
  });

  const renderNode = (node, level) => {
    const org = node.data;
    const children = node.children;
    return (
      <StyledTreeItem
        key={org.organizationId}
        nodeId={org.organizationId}
        label={org.organizationName}
        ContentComponent={TreeNode}
        level={level}
      >
        {children.length
          ? children.map((childNode) => renderNode(childNode, level + 1))
          : null}
      </StyledTreeItem>
    );
  };

  return (
    <TreeView
      defaultCollapseIcon={
        <ArrowDropDown
          sx={{ cursor: 'pointer', width: '20px', height: '20px' }}
        />
      }
      defaultExpandIcon={
        <ArrowRight sx={{ cursor: 'pointer', width: '20px', height: '20px' }} />
      }
      defaultExpanded={organizations.map((org) => org.organizationId)}
    >
      {tree.map((node) => renderNode(node, 1))}
    </TreeView>
  );
};
